// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {GroupDMAvatar} from '@app/features/app/components/shared/GroupDMAvatar';
import type {Channel} from '@app/features/channel/models/Channel';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import styles from '@app/features/guild/components/modals/DuplicateGroupConfirmModal.module.css';
import {focusChannelTextareaAfterNavigation} from '@app/features/messaging/utils/ChannelTextareaFocusUtils';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {formatShortRelativeTime} from '@app/vendor/date_utils/DateDuration';
import * as SnowflakeUtils from '@app/vendor/snowflake/SnowflakeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

interface DuplicateGroupConfirmModalProps {
	channels: Array<Channel>;
	onConfirm: () => Promise<void> | void;
}
export const DuplicateGroupConfirmModal = observer(({channels, onConfirm}: DuplicateGroupConfirmModalProps) => {
	const {t} = useLingui();
	const handleChannelClick = useCallback((channelId: string) => {
		ModalCommands.pop();
		NavigationCommands.selectChannel(undefined, channelId);
		focusChannelTextareaAfterNavigation(channelId);
	}, []);
	const description = useMemo(() => {
		return (
			<>
				<p className={styles.description}>
					<Trans>
						You already have a group with these users. Do you really want to create a new one? That&apos;s fine too!
					</Trans>
				</p>
				{channels.length > 0 && (
					<div className={styles.channelList}>
						{channels.map((channel) => {
							const lastActivitySnowflake = channel.lastMessageId ?? channel.id;
							const lastActiveText = formatShortRelativeTime(SnowflakeUtils.extractTimestamp(lastActivitySnowflake));
							const lastActiveLabel = lastActiveText || t`No activity yet`;
							return (
								<FocusRing key={channel.id} offset={-2}>
									<button type="button" className={styles.channelItem} onClick={() => handleChannelClick(channel.id)}>
										<div className={styles.avatarWrapper}>
											<GroupDMAvatar channel={channel} size={40} />
										</div>
										<div className={styles.channelDetails}>
											<span className={styles.channelName}>{ChannelUtils.getDMDisplayName(channel)}</span>
											<span className={styles.lastActive}>{lastActiveLabel}</span>
										</div>
									</button>
								</FocusRing>
							);
						})}
					</div>
				)}
			</>
		);
	}, [channels, handleChannelClick]);
	return (
		<ConfirmModal
			title={t`Confirm New Group`}
			description={description}
			primaryText={t`Create new group`}
			primaryVariant="primary"
			secondaryText={t`Cancel`}
			size="small"
			onPrimary={onConfirm}
		/>
	);
});
